import React, { useState, useEffect } from 'react';
import { Route, Routes, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Navbar, Form, FormControl, Dropdown, Container, Button, Spinner, Alert, Pagination } from 'react-bootstrap';
import ComicList from '../pages/ComicList/ComicList';
import ComicDetail from '../pages/ComicDetail/ComicDetail';
import ChapterView from '../pages/ChapterView/ChapterView';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { HomeIcon, BookIcon } from '../components/Icons/Icons';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

const AppContent = ({ user }) => {
  const [comics, setComics] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('update_time');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  
  const isHomePage = location.pathname === '/';
  const isChapterViewPage = /^\/comic\/[^/]+\/chapter\/[^/]+$/.test(location.pathname);

  const limit = 20; // Number of comics per page

  useEffect(() => {
    const loadComics = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!user) {
          return;
        }
        const token = await user.getIdToken();

        const response = await fetch(`${backendUrl}/api/comics?page=${page}&limit=${limit}&sortOrder=${sortOrder}&searchTerm=${searchTerm}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-API-Key': process.env.REACT_APP_API_KEY
          }
        });
        if (!response.ok) throw new Error("Failed to load comics data.");
        const data = await response.json();
        setComics(data.comics);
        setPage(data.page);
        setPages(data.pages);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadComics();
  }, [page, sortOrder, searchTerm, user]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleSort = (sortKey) => {
    setSortOrder(sortKey);
    setPage(1);
  };

  const navigateUp = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    if (pathSegments.length === 4 && pathSegments[0] === 'comic' && pathSegments[2] === 'chapter') {
      navigate(`/comic/${pathSegments[1]}`);
    } else if (pathSegments.length === 2 && pathSegments[0] === 'comic') {
      navigate('/');
    } else {
      navigate(-1);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout error", error);
    }
  };

  // --- NEW FUNCTION TO RENDER PAGINATION WITH ELLIPSIS ---
  const renderPaginationItems = () => {
    const pageNeighbours = 1; // How many pages to show on each side of the current page
    const paginationItems = [];
    
    // The total number of blocks to show is 2 (first/last) + 1 (current) + 2*neighbours + 2 (ellipsis)
    const totalBlocks = (pageNeighbours * 2) + 5; 

    if (pages > totalBlocks) {
      const startPage = Math.max(2, page - pageNeighbours);
      const endPage = Math.min(pages - 1, page + pageNeighbours);

      // Always show the first page
      paginationItems.push(
        <Pagination.Item key={1} active={1 === page} onClick={() => setPage(1)}>
          1
        </Pagination.Item>
      );

      // Show ellipsis if there's a gap after the first page
      if (startPage > 2) {
        paginationItems.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }

      // Show pages around the current page
      for (let i = startPage; i <= endPage; i++) {
        paginationItems.push(
          <Pagination.Item key={i} active={i === page} onClick={() => setPage(i)}>
            {i}
          </Pagination.Item>
        );
      }

      // Show ellipsis if there's a gap before the last page
      if (endPage < pages - 1) {
        paginationItems.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      
      // Always show the last page
      paginationItems.push(
        <Pagination.Item key={pages} active={pages === page} onClick={() => setPage(pages)}>
          {pages}
        </Pagination.Item>
      );

    } else {
      // If there aren't too many pages, show all of them
      for (let i = 1; i <= pages; i++) {
        paginationItems.push(
          <Pagination.Item key={i} active={i === page} onClick={() => setPage(i)}>
            {i}
          </Pagination.Item>
        );
      }
    }
    
    return paginationItems;
  };


  return (
    <>
      <Navbar bg="dark" variant="dark" expand="lg" className="mb-4">
        <Container>
          <Navbar.Brand as={Link} to="/">Comic Book Reader</Navbar.Brand>
          {isHomePage ? (
            <>
              <Navbar.Toggle aria-controls="responsive-navbar-nav" />
              <Navbar.Collapse id="responsive-navbar-nav">
                <div className="ms-auto d-grid d-lg-flex gap-2 align-items-center mt-2 mt-lg-0">
                  <Form className="d-flex">
                    <FormControl
                      type="text"
                      placeholder="Search by title or book ID"
                      value={searchTerm}
                      onChange={handleSearch}
                    />
                  </Form>
                  <Dropdown className="d-grid">
                    <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                      Sort By: {
                        sortOrder === 'update_time' ? 'Updated Time' :
                        sortOrder === 'publishedTime' ? 'Published Time' :
                        sortOrder === 'title' ? 'Title' :
                        sortOrder === 'book_id_desc' ? 'Book ID (Desc)' :
                        sortOrder === 'book_id' || sortOrder === 'book_id_asc' ? 'Book ID (Asc)' :
                        'Updated Time'
                      }
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => handleSort('update_time')} active={sortOrder === 'update_time'}>Updated Time</Dropdown.Item>
                      <Dropdown.Item onClick={() => handleSort('publishedTime')} active={sortOrder === 'publishedTime'}>Published Time</Dropdown.Item>
                      <Dropdown.Item onClick={() => handleSort('title')} active={sortOrder === 'title'}>Title</Dropdown.Item>
                      <Dropdown.Item onClick={() => handleSort('book_id_asc')} active={sortOrder === 'book_id_asc' || sortOrder === 'book_id'}>Book ID (Ascending)</Dropdown.Item>
                      <Dropdown.Item onClick={() => handleSort('book_id_desc')} active={sortOrder === 'book_id_desc'}>Book ID (Descending)</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <Button variant="outline-light" onClick={handleLogout}>Logout</Button>
                </div>
              </Navbar.Collapse>
            </>
          ) : (
            <Button variant="outline-light" onClick={() => navigateUp()} className="ms-auto">
              {isChapterViewPage ? <BookIcon /> : <HomeIcon />}
            </Button>
          )}
        </Container>
      </Navbar>
      <main>
        {loading && <div className="loading-container"><Spinner /></div>}
        {error && <div className="error-container"><Alert variant="danger">{error}</Alert></div>}
        {!loading && !error && (
          <>
            <Routes>
              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route path="/" element={<ComicList comics={comics} />} />
              <Route path="/comic/:book_id" element={<ComicDetail />} />
              <Route path="/comic/:book_id/chapter/:chapter_id" element={<ChapterView />} />
            </Routes>
            {isHomePage && pages > 1 && (
              <div className="d-flex justify-content-center align-items-center my-4">
                <Pagination>
                  {/* --- UPDATED PAGINATION BLOCK --- */}
                  <Pagination.First onClick={() => setPage(1)} disabled={page <= 1} />
                  <Pagination.Prev onClick={() => setPage(page - 1)} disabled={page <= 1} />
                  
                  {renderPaginationItems()}

                  <Pagination.Next onClick={() => setPage(page + 1)} disabled={page >= pages} />
                  <Pagination.Last onClick={() => setPage(pages)} disabled={page >= pages} />
                </Pagination>
              </div>
            )}
          </>
        )}
      </main>
    </>
  );
};

export default AppContent;
